import { useCallback } from 'react';
import { useObserver } from 'mobx-react-lite';
import { useApolloClient } from '@apollo/client';
import { useIdleTimerContext } from 'react-idle-timer';

import useCart from 'src/hooks/use-cart';
import useUI from 'src/hooks/use-ui';
import useStores from 'shared/hooks/use-stores';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';
import { teardownKioskSession } from 'src/components/kiosk-pro/kiosk-pro-shared/kiosk-session-teardown';

// A hung logout must not leave the last shopper's session on screen: redirect
// after this long even if the teardown has not settled.
const TEARDOWN_TIMEOUT_MS = 3000;

/**
 * The kiosk session reset the shared idle warning already performs: dismiss
 * kiosk surfaces, empty the cart, log out, and send the terminal to the lock
 * screen (which is where concierge conversation ids get cleared). Extracted so
 * the concierge warning delegates to it instead of reimplementing a reset.
 *
 * On Kiosk Pro the reset is the full `teardownKioskSession` (the same one End
 * Session runs), so the shopper, cart, caches and Concierge storage are all
 * cleared before the terminal lands on the lock screen.
 *
 * Each step is guarded: a failure in one must not strand the terminal on a
 * half-reset page, so the redirect always runs.
 */
export function useKioskSessionReset(): () => void {
  const Cart = useCart();
  const { clearOrder } = Cart;
  const UI = useUI();
  const { User, Modals } = useStores();
  const apolloClient = useApolloClient();
  const idleTimer = useIdleTimerContext();
  const isKioskPro = useKioskPro();
  const { dispensary } = useDispensary();
  const cName = dispensary?.cName;
  const isUserLoggedIn = useObserver(() => Boolean(User?.isLoggedIn));

  return useCallback(() => {
    if (isKioskPro) {
      // teardownKioskSession guards each step itself; the catch is a last resort
      // so the terminal always reaches the lock screen, also if the teardown hangs.
      let hasRedirected = false;
      const redirect = (): void => {
        if (hasRedirected) return;
        hasRedirected = true;
        clearTimeout(timeout);
        UI.redirectToKioskLockScreen(cName);
      };
      const timeout = setTimeout(redirect, TEARDOWN_TIMEOUT_MS);
      void teardownKioskSession({
        user: User,
        cart: Cart,
        ui: UI,
        apolloClient,
        idleTimer,
        dispensaryId: dispensary?.id,
        modals: Modals,
      })
        .catch(() => undefined)
        .then(redirect);
      return;
    }

    // Each step is guarded independently: a try/finally around the whole
    // sequence would still propagate a clearOrder() throw after the finally
    // ran, skipping logout() and rethrowing out of the callback (exactly the
    // bug this delegate exists to fix). Nothing here may abort a later step
    // or the redirect.
    try {
      UI.showKioskCheckoutModal = false;
      UI.setIsKioskProLoginModalOpen(false);
      UI.closeCartDrawer();
      UI.closeCartInterstitial();
    } catch {
      /* surfacing kiosk chrome state must never block cart/logout/redirect */
    }

    try {
      clearOrder();
    } catch {
      /* an unclearable cart must not skip logout or the redirect below */
    }

    try {
      if (isUserLoggedIn) {
        void User?.logout();
      }
    } catch {
      /* a failed logout must not abort the redirect below */
    }

    UI.redirectToKioskLockScreen(cName);
  }, [isKioskPro, UI, User, Cart, Modals, apolloClient, idleTimer, dispensary?.id, clearOrder, isUserLoggedIn, cName]);
}
